import {
	Grid,
	TextField,
	Card,
	CardContent,
} from "../../../common/MaterialComponent";
import React, { useContext, useEffect, useState } from "react";
import {
	FailureActionType,
	IVRFlowTypeEnum,
	WidgetTypeEnum,
} from "../../../common/enum";
import { INextActionWidget } from "../../../common/interfaces";

import {
	FlowWizardState,
	GetNextActionWidgetList,
	IVRFlowWizardContext,
	WidgetPropertyType,
	WizardStepperProp,
	GetSelectedLanguageList,
	IsAudioConfigurationAllowed,
} from "../../../hooks/useIVRFlowWidgetConfiguration";
import AskQuestionWidgetModel from "../../../models/RequestForHelpTemplates/AskQuestionWidgetModel";
import ResponseMapModel from "../../../models/RequestForHelpTemplates/ResponseMapModel";
import WidgetLanguageDetailModel from "../../../models/RequestForHelpTemplates/WidgetLanguageDetailModel";
import { FailureActionConfiguration } from "../FailureActionConfiguration/FailureActionConfiguration";
import { MultiLanguageWidget } from "../MultiLanguageWidget/MultiLanguageWidget";
import { NextActionSelection } from "../NextActionSelection/NextActionSelection";
import {
	ResponseMapPropertyType,
	ResponseMapWidget,
} from "../ResponseMapWidget/ResponseMapWidget";
import "./../IVRFlowConfiguration/IVRFlowConfiguration.css";
import AudioFileModel from "../../../models/RequestForHelpTemplates/AudioFileModel";
import { MultiAudioUploadWidget } from "../MultiAudioUploadWidget/MultiAudioUploadWidget";
import Shared from "../../../common/shared";
import { useGetComponentStringsContext } from "../../../hooks/useGetComponentStrings";

export type AskQuestionType =
	| "label"
	| "variableName"
	| "timeout"
	| "responseMap"
	| "maxRetries"
	| "failureActionType"
	| "failureDefaultValue"
	| "isVoiceNoteAllowed"
	| AskQuestionTextType
	| AskQuestionAudioType
	| "nextAction";

export type AskQuestionTextType =
	| "questionText"
	| "retryMessage"
	| "failureMessage";

export type AskQuestionAudioType =
	| "questionAudioFile"
	| "retryAudioFile"
	| "failureAudioFile";

export const AskQuestionWidget: React.FC<WizardStepperProp> = ({
	widgetId,
	ivrFlowType,
	highlightRequiredDetail,
}) => {
	const [
		askQuestionWidgetDetail,
		setAskQuestionWidgetDetail,
	] = useState<AskQuestionWidgetModel>(new AskQuestionWidgetModel());

	const createNewWidgetId = "CreateNewWidget";

	const { state, dispatch } = useContext(IVRFlowWizardContext);
	const strings = useGetComponentStringsContext().AskQuestionWidget;

	const selectedLanguageList = () => {
		return GetSelectedLanguageList(
			(state as FlowWizardState).wizardConfig.pickLanguageWidgetDetail
		);
	};

	const updateWidgetDetail = (
		propertyName: AskQuestionType,
		propertyValue: WidgetPropertyType
	) => {
		// dispatch action to update widget detail
		dispatch({
			type: "UPDATE_WIDGET_DETAIL",
			payload: {
				widgetId: askQuestionWidgetDetail.id,
				widgetType: askQuestionWidgetDetail.type as WidgetTypeEnum,
				propertyName: propertyName,
				propertyValue: propertyValue,
			},
		});
	};

	const onWidgetDetailChange = (
		value: number | string,
		propertyName: AskQuestionType
	) => {
		const updatedInputValue =
			typeof value === "string" ? Shared.getSanitizedInputValue(value) : value;
		const askQuestionObj: AskQuestionWidgetModel = {
			...askQuestionWidgetDetail,
		};

		if (propertyName === "variableName") {
			// regex for not allowing space
			const regex = /^\S*$/;
			if (!regex.test(updatedInputValue.toString())) {
				return;
			}
		}

		(askQuestionObj[propertyName] as string | number) = updatedInputValue;
		setAskQuestionWidgetDetail(askQuestionObj);
		updateWidgetDetail(
			propertyName,
			askQuestionObj[propertyName] as WidgetPropertyType
		);

		// update failureActionConfiguration in state on failureActionType change
		if (propertyName === "failureActionType") {
			if (updatedInputValue === FailureActionType.UseDefaultValue) {
				updateWidgetDetail(
					"failureDefaultValue",
					askQuestionObj.failureDefaultValue!
				);
			} else if (updatedInputValue === FailureActionType.SendMessageAndExit) {
				updateWidgetDetail("failureMessage", askQuestionObj.failureMessage);
				updateWidgetDetail(
					"failureAudioFile",
					askQuestionObj.failureAudioFile!
				);
			}
		}
	};

	const onWidgetTextDetailChange = (
		value: WidgetLanguageDetailModel,
		propertyName: AskQuestionTextType
	) => {
		const askQuestionObj: AskQuestionWidgetModel = {
			...askQuestionWidgetDetail,
		};
		const askQuestionTemp: WidgetLanguageDetailModel[] =
			askQuestionObj[propertyName];

		const index: number = askQuestionTemp.findIndex(
			(model: WidgetLanguageDetailModel) => model.language === value.language
		);
		if (index > -1) {
			askQuestionTemp.splice(index, 1, value);
		} else {
			askQuestionTemp.push(value);
		}
		setAskQuestionWidgetDetail(askQuestionObj);
		updateWidgetDetail(propertyName, askQuestionObj[propertyName]);
	};

	const onWidgetAudioFileChange = (
		value: AudioFileModel,
		propertyName: AskQuestionAudioType
	) => {
		const askQuestionObj: AskQuestionWidgetModel = {
			...askQuestionWidgetDetail,
		};
		const askQuestionTemp: AudioFileModel[] = askQuestionObj[propertyName];

		const index: number = askQuestionTemp.findIndex(
			(model: AudioFileModel) => model.language === value.language
		);
		if (index > -1) {
			askQuestionTemp.splice(index, 1, value);
		} else {
			askQuestionTemp.push(value);
		}
		setAskQuestionWidgetDetail(askQuestionObj);
		updateWidgetDetail(propertyName, askQuestionObj[propertyName]);
	};

	useEffect(() => {
		const widgetDetail = (state as FlowWizardState).wizardConfig
			.askQuestionWidgetDetail;
		const index = widgetDetail?.findIndex((widget) => widget.id === widgetId);

		if (index !== -1 && widgetDetail) {
			setAskQuestionWidgetDetail(widgetDetail[index]!);
		}
		// disabled eslint rule as useEffect needs to be called once on page load
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const addNewResponseMapDetail = () => {
		const askQuestionObj: AskQuestionWidgetModel = {
			...askQuestionWidgetDetail,
		};
		askQuestionObj.responseMap.push(new ResponseMapModel());
		setAskQuestionWidgetDetail(askQuestionObj);
		updateWidgetDetail("responseMap", askQuestionObj.responseMap);
	};

	const updateResponseMapDetail = (
		value: string,
		propertyName: ResponseMapPropertyType,
		index: number
	) => {
		const askQuestionObj: AskQuestionWidgetModel = {
			...askQuestionWidgetDetail,
		};
		askQuestionObj.responseMap[index][propertyName] =
			value === createNewWidgetId ? "" : value;
		setAskQuestionWidgetDetail(askQuestionObj);
		updateWidgetDetail("responseMap", askQuestionObj.responseMap);
		if (value === createNewWidgetId) {
			handleCreateWidgetAction();
		}
	};

	const deleteResponseMapDetail = (index: number) => {
		const askQuestionObj: AskQuestionWidgetModel = {
			...askQuestionWidgetDetail,
		};
		askQuestionObj.responseMap.splice(index, 1);

		setAskQuestionWidgetDetail(askQuestionObj);
		updateWidgetDetail("responseMap", askQuestionObj.responseMap);
	};

	const handleNextActionSelection = (value: string) => {
		const askQuestionObj: AskQuestionWidgetModel = {
			...askQuestionWidgetDetail,
		};
		askQuestionObj.nextAction = value === createNewWidgetId ? "" : value;
		setAskQuestionWidgetDetail(askQuestionObj);
		updateWidgetDetail("nextAction", askQuestionObj.nextAction);
		if (value === createNewWidgetId) {
			handleCreateWidgetAction();
		}
	};

	const nextActionWidgetList = () => {
		// pass stepper content list and selected widget type
		const widgetList: INextActionWidget[] = GetNextActionWidgetList(
			(state as FlowWizardState).stepperContent,
			askQuestionWidgetDetail.id
		);
		return widgetList;
	};

	const handleCreateWidgetAction = () => {
		// Create New Widget option is selected, dispatch action to open widget selection menu
		dispatch({
			type: "HANDLE_CREATE_WIDGET",
			payload: {},
		});
	};

	const handleIsVoiceNoteAllowedChange = (isChecked: boolean) => {
		const askQuestionObj: AskQuestionWidgetModel = {
			...askQuestionWidgetDetail,
		};
		askQuestionObj.isVoiceNoteAllowed = isChecked;
		setAskQuestionWidgetDetail(askQuestionObj);
		updateWidgetDetail(
			"isVoiceNoteAllowed",
			askQuestionObj.isVoiceNoteAllowed as WidgetPropertyType
		);
	};

	return (
		<>
			<Card variant="outlined">
				<CardContent>
					<Grid container spacing={1}>
						<Grid item xs={12}>
							<TextField
								label={strings.widgetLabel}
								value={askQuestionWidgetDetail.label}
								onChange={(event) =>
									onWidgetDetailChange(event.target.value as string, "label")
								}
								fullWidth
								required
								error={
									!askQuestionWidgetDetail.label && highlightRequiredDetail
								}
								helperText={
									!askQuestionWidgetDetail.label && highlightRequiredDetail
										? strings.labelErrorMessage
										: ""
								}
							/>
						</Grid>
						<Grid item xs={12}>
							<TextField
								label={strings.variableName}
								value={askQuestionWidgetDetail.variableName}
								onChange={(event) =>
									onWidgetDetailChange(
										event.target.value as string,
										"variableName"
									)
								}
								fullWidth
								required
								error={
									!askQuestionWidgetDetail.variableName &&
									highlightRequiredDetail
								}
								helperText={
									!askQuestionWidgetDetail.variableName &&
									highlightRequiredDetail
										? strings.variableNameErrorMessage
										: ""
								}
							/>
						</Grid>
						{ivrFlowType !== IVRFlowTypeEnum.USSD && (
							<Grid item xs={12}>
								<TextField
									label={strings.timeOut}
									value={askQuestionWidgetDetail.timeout}
									onChange={(event) =>
										onWidgetDetailChange(
											event.target.value ? Number(event.target.value) : "",
											"timeout"
										)
									}
									type="number"
									fullWidth
									required
									error={
										!askQuestionWidgetDetail.timeout && highlightRequiredDetail
									}
									helperText={
										!askQuestionWidgetDetail.timeout && highlightRequiredDetail
											? strings.timeoutErrorMessage
											: ""
									}
								/>
							</Grid>
						)}
						<MultiLanguageWidget
							title={strings.questionText}
							widgetLanguageDetail={askQuestionWidgetDetail.questionText}
							selectedLanguageList={selectedLanguageList()}
							onMessageDetailChange={(
								questionText: WidgetLanguageDetailModel
							) => onWidgetTextDetailChange(questionText, "questionText")}
							highlightRequiredDetail={highlightRequiredDetail}
							isValidationRequired={true}
						/>
						{IsAudioConfigurationAllowed(ivrFlowType) && (
							<MultiAudioUploadWidget
								title={strings.questionAudioFile}
								widgetLanguageDetail={askQuestionWidgetDetail.questionAudioFile}
								selectedLanguageList={selectedLanguageList()}
								partitionKey={(state as FlowWizardState).partitionKey}
								rowKey={(state as FlowWizardState).rowKey}
								handleAudioFileDetailChange={(object: AudioFileModel) =>
									onWidgetAudioFileChange(object, "questionAudioFile")
								}
							/>
						)}
						<ResponseMapWidget
							responseMap={askQuestionWidgetDetail.responseMap}
							widgetList={nextActionWidgetList()}
							addNewResponseMapDetail={addNewResponseMapDetail}
							updateResponseMapDetail={updateResponseMapDetail}
							deleteResponseMapDetail={deleteResponseMapDetail}
							widgetType={askQuestionWidgetDetail.type}
							ivrFlowType={ivrFlowType}
							isVoiceNoteAllowed={askQuestionWidgetDetail.isVoiceNoteAllowed}
							handleIsVoiceNoteAllowedChange={handleIsVoiceNoteAllowedChange}
							highlightRequiredDetail={highlightRequiredDetail}
						/>
						<Grid item xs={12}>
							<TextField
								label={strings.maxRetries}
								value={askQuestionWidgetDetail.maxRetries}
								onChange={(event) =>
									onWidgetDetailChange(
										event.target.value ? Number(event.target.value) : "",
										"maxRetries"
									)
								}
								type="number"
								fullWidth
							/>
						</Grid>
						<MultiLanguageWidget
							title={strings.retryMessage}
							widgetLanguageDetail={askQuestionWidgetDetail.retryMessage}
							selectedLanguageList={selectedLanguageList()}
							onMessageDetailChange={(
								retryMessage: WidgetLanguageDetailModel
							) => onWidgetTextDetailChange(retryMessage, "retryMessage")}
						/>
						{IsAudioConfigurationAllowed(ivrFlowType) && (
							<MultiAudioUploadWidget
								title={strings.retryAudioFile}
								widgetLanguageDetail={askQuestionWidgetDetail.retryAudioFile}
								selectedLanguageList={selectedLanguageList()}
								partitionKey={(state as FlowWizardState).partitionKey}
								rowKey={(state as FlowWizardState).rowKey}
								handleAudioFileDetailChange={(object: AudioFileModel) =>
									onWidgetAudioFileChange(object, "retryAudioFile")
								}
							/>
						)}
						<FailureActionConfiguration
							failureActionType={askQuestionWidgetDetail.failureActionType}
							failureDefaultValue={askQuestionWidgetDetail.failureDefaultValue}
							failureMessage={askQuestionWidgetDetail.failureMessage}
							failureAudioFile={askQuestionWidgetDetail.failureAudioFile}
							selectedLanguageList={selectedLanguageList()}
							partitionKey={(state as FlowWizardState).partitionKey}
							rowKey={(state as FlowWizardState).rowKey}
							ivrFlowType={ivrFlowType}
							handleFailureActionConfigChange={(value: string, type: string) =>
								onWidgetDetailChange(value, type as AskQuestionType)
							}
							handleFailureLanguageConfigChange={(
								value: WidgetLanguageDetailModel,
								propertyName: string
							) =>
								onWidgetTextDetailChange(
									value,
									propertyName as AskQuestionTextType
								)
							}
							handleAudioFileChange={(object: AudioFileModel) =>
								onWidgetAudioFileChange(object, "failureAudioFile")
							}
							highlightRequiredDetail={highlightRequiredDetail}
						/>
						<Grid item xs={12}>
							<NextActionSelection
								widgetList={nextActionWidgetList()}
								selectedWidget={askQuestionWidgetDetail.nextAction}
								onNextActionChange={(value: string) =>
									handleNextActionSelection(value)
								}
								highlightRequiredDetail={highlightRequiredDetail}
								isValidationRequired={
									!askQuestionWidgetDetail.responseMap.length ||
									askQuestionWidgetDetail.responseMap.filter(
										(r) => r.nextAction === ""
									).length > 0
								}
							/>
						</Grid>
					</Grid>
				</CardContent>
			</Card>
		</>
	);
};
